import { useState, useRef } from 'react';
import { Box, TextField, Button, Paper, Slider, Typography } from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import toast from 'react-hot-toast';
import ColorPicker from './ColorPicker';

function QRGenerator() {
  const [url, setUrl] = useState('');
  const [fgColor, setFgColor] = useState('#6366f1');
  const [bgColor, setBgColor] = useState('#ffffff');
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [blur, setBlur] = useState(0);
  const qrRef = useRef();

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setLogo(ev.target.result);
        setLogoPreview(ev.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      setLogo(null);
      setLogoPreview(null);
    }
  };

  const handleDownload = () => {
    if (!url) {
      toast.error('Please enter a URL first');
      return;
    }

    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;

    if (logo) {
      // Create a temp canvas to merge background image (with blur) and QR code
      const size = canvas.width;
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = size;
      tempCanvas.height = size;
      const tempCtx = tempCanvas.getContext('2d');

      // Draw blurred background image
      const img = new window.Image();
      img.onload = () => {
        tempCtx.filter = `blur(${blur}px)`;
        tempCtx.drawImage(img, 0, 0, size, size);
        tempCtx.filter = 'none';

        // Draw QR code pattern (from original canvas) on top
        tempCtx.drawImage(canvas, 0, 0, size, size);

        // Download merged result
        const image = tempCanvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = image;
        link.download = 'qr-color-code.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('QR code downloaded!');
      };
      img.src = logo;
    } else {
      const image = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = image;
      link.download = 'qr-color-code.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('QR code downloaded!');
    }
  };

  return (
    <Box
      component={Paper}
      elevation={2}
      sx={{
        p: 3,
        borderRadius: 3,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 4,
      }}
    >
      <Box sx={{ flex: 1 }}>
        <TextField
          fullWidth
          label="Enter URL"
          variant="outlined"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          sx={{ mb: 3 }}
        />

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <ColorPicker
            label="QR Code Color"
            value={fgColor}
            onChange={setFgColor}
          />
          {!logo && (
            <ColorPicker
              label="Background Color"
              value={bgColor}
              onChange={setBgColor}
            />
          )}
        </Box>

        <Box sx={{ mb: 2 }}>
          <input
            accept="image/*"
            type="file"
            id="logo-upload"
            style={{ display: 'none' }}
            onChange={handleLogoChange}
          />
          <label htmlFor="logo-upload">
            <Button variant="outlined" component="span" fullWidth>
              {logoPreview ? 'Change Logo/Image' : 'Upload Logo/Image'}
            </Button>
          </label>
          {logoPreview && (
            <>
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                <img
                  src={logoPreview}
                  alt="Logo Preview"
                  style={{ maxWidth: 60, maxHeight: 60, borderRadius: 8, border: '1px solid #eee' }}
                />
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography gutterBottom>Background Blur</Typography>
                <Slider
                  value={blur}
                  onChange={(_, v) => setBlur(v)}
                  min={0}
                  max={20}
                  step={1}
                  valueLabelDisplay="auto"
                  aria-label="Background Blur"
                />
              </Box>
            </>
          )}
        </Box>

        <Button
          variant="contained"
          size="large"
          startIcon={<FileDownloadIcon />}
          onClick={handleDownload}
          fullWidth
        >
          Download QR Code
        </Button>
      </Box>

      <Box
        ref={qrRef}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 4,
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <QRCodeCanvas
          value={url || 'https://qrcolor.xyz'}
          size={200}
          level="H"
          fgColor={fgColor}
          bgColor={logo ? 'rgba(0,0,0,0)' : bgColor}
          includeMargin
        />
      </Box>
    </Box>
  );
}

export default QRGenerator;
